import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Calculator, FileXls, ArrowUp, ArrowDown, Equals } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VATPage = ({ embedded }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vat/summary?month=${selectedMonth}`, { withCredentials: true });
      setSummary(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, API_URL]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const exportExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vat/export/excel?month=${selectedMonth}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `synthese_tva_${selectedMonth}.xlsx`;
      link.click();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur export');
    }
  };

  // Generate months for selector
  const months = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(format(d, 'yyyy-MM'));
  }

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="vat-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Synthèse TVA</h1>
          <p className="page-subtitle">Calcul TVA collectée - TVA déductible</p>
        </div>
        <button className="btn btn-primary" onClick={exportExcel}>
          <FileXls size={18} /> Export Excel
        </button>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button className="btn btn-primary" onClick={exportExcel}>
            <FileXls size={18} /> Export Excel
          </button>
        </div>
      )}

      {/* Month Selector */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span className="label-text">Période :</span>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger style={{ width: '180px' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {summary && (
        <>
          {/* TVA Collected */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header" style={{ backgroundColor: '#E8F5E9' }}>
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2E7D32' }}>
                <ArrowUp size={20} weight="bold" />
                TVA Collectée (Chantiers)
              </h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                <div>
                  <div className="label-text" style={{ marginBottom: '8px' }}>Total facturé HT</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{summary.total_invoiced_ht.toLocaleString()}€</div>
                </div>
                <div>
                  <div className="label-text" style={{ marginBottom: '8px' }}>TVA collectée</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2E7D32' }}>{summary.tva_collected.toLocaleString()}€</div>
                </div>
              </div>
            </div>
          </div>

          {/* TVA Deductible */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header" style={{ backgroundColor: '#FFEBEE' }}>
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#C62828' }}>
                <ArrowDown size={20} weight="bold" />
                TVA Déductible
              </h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                <div>
                  <div className="label-text" style={{ marginBottom: '8px' }}>Dépenses entreprise HT</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>{summary.total_expenses_ht.toLocaleString()}€</div>
                  <div style={{ fontSize: '0.875rem', color: '#C62828', marginTop: '4px' }}>
                    TVA: {summary.tva_deductible_expenses.toLocaleString()}€
                  </div>
                </div>
                <div>
                  <div className="label-text" style={{ marginBottom: '8px' }}>Dépenses chantiers HT</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>{summary.total_project_expenses_ht.toLocaleString()}€</div>
                  <div style={{ fontSize: '0.875rem', color: '#C62828', marginTop: '4px' }}>
                    TVA: {summary.tva_deductible_projects.toLocaleString()}€
                  </div>
                </div>
                <div style={{ borderLeft: '2px solid #E2E2E6', paddingLeft: '24px' }}>
                  <div className="label-text" style={{ marginBottom: '8px' }}>Total TVA déductible</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#C62828' }}>
                    {summary.total_tva_deductible.toLocaleString()}€
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Balance */}
          <div className="card" style={{ 
            backgroundColor: summary.tva_balance >= 0 ? '#E3F2FD' : '#E8F5E9',
            border: `2px solid ${summary.tva_balance >= 0 ? '#1565C0' : '#2E7D32'}`
          }}>
            <div className="card-body" style={{ padding: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: summary.tva_balance >= 0 ? '#1565C0' : '#2E7D32',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Equals size={32} color="white" weight="bold" />
                  </div>
                  <div>
                    <div className="label-text" style={{ marginBottom: '4px' }}>Solde TVA</div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: summary.tva_balance >= 0 ? '#1565C0' : '#2E7D32' }}>
                      {Math.abs(summary.tva_balance).toLocaleString()}€
                    </div>
                  </div>
                </div>
                <div style={{
                  padding: '12px 24px',
                  backgroundColor: summary.tva_balance >= 0 ? '#1565C0' : '#2E7D32',
                  color: 'white',
                  borderRadius: '4px',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}>
                  {summary.tva_balance >= 0 ? 'TVA à payer' : 'Crédit de TVA'}
                </div>
              </div>

              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '0.875rem', color: '#5E5F66' }}>
                  <strong>Calcul :</strong> TVA collectée ({summary.tva_collected.toLocaleString()}€) - TVA déductible ({summary.total_tva_deductible.toLocaleString()}€) = <strong>{summary.tva_balance.toLocaleString()}€</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div style={{ 
            marginTop: '24px', 
            padding: '16px 24px', 
            backgroundColor: '#FFF8E1', 
            border: '1px solid #FFE082',
            borderRadius: '4px',
            fontSize: '0.875rem',
            color: '#F57F17'
          }}>
            <strong>Note :</strong> Cette synthèse est basée sur les données saisies dans les modules "Gestion de Chantiers" (TVA collectée sur les situations travaux) et "Dépenses" (TVA déductible). Exportez en Excel pour transmettre à votre comptable.
          </div>
        </>
      )}
    </div>
  );
};

export default VATPage;
